/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, Image } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faGlobe,
    faEnvelope,
    faPhone,
    faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import Spinner from 'react-bootstrap/Spinner';
import styled from 'styled-components';

import companyIcon from '../../../../common/assets/img/ToolbeltIcons_Artboard_19.png'
import { APP_COLORS } from '../../../../common/Constants';
import * as CubeApi from '../../../../common/CubeApi';
import config from '../../../../config';
import ExploreMapFlyoutDetailUnCluster from "./ExploreMapFlyoutDetailUnCluster";
import {useCubeNamespace} from "../../../../common/utils/universeUtils";

// --- Styled Components ---
const LeadTextSmall = styled.span`
    font-size: 11px;
    padding-left: 5px;
    font-style: italic;
    color: ${APP_COLORS['gray-600']};
`;

const TextLink = styled.a`
    font-size: 12px;
    padding-left: 5px;
    font-style: italic;
    color: ${APP_COLORS['tech-blue']};
`;

const TextLinkContainer = styled.div`
    margin-top: 10px;
    display: grid;
    grid-template-columns: 25px 25px 25px 25px;
`;

const ScoreText = styled.span`
    font-size: 12px;
    font-weight: 400;
    font-style: italic;
    color: ${APP_COLORS['gray-card']};
    margin-left: 5px;
`;

export default function ExploreMapFlyoutDetailSelectedProductRecord({ selectedPoint }) {
    const universeState = useSelector(state => state.universe);
    const currentUniverse = universeState.currentUniverse || 'BUSINESS';
    const exploreByContact = universeState.universes[currentUniverse]?.exploreByContact ?? false;

    const cubeNamespace = useCubeNamespace();

    const [loading, setLoading] = useState(true);
    const [record, setRecord] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!selectedPoint) return;

        setLoading(true);
        setError(null);

        _getDataPointRecord(cubeNamespace, selectedPoint)
            .then((data) => {
                setRecord(data);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error loading point record:", err);
                setError("Failed to load details.");
                setLoading(false);
            });
    }, [
        selectedPoint?.properties?.id,
        selectedPoint?.properties?.companyId,
        selectedPoint?.properties?.company_identity,
        selectedPoint?.properties?.company_address_city,
        selectedPoint?.geometry?.coordinates?.[0],
        selectedPoint?.geometry?.coordinates?.[1],
        currentUniverse,
        exploreByContact,
        cubeNamespace
    ]);

    // --- Render Logic ---
    if (loading) {
        return (
            <Row>
                <Col>
                    <div className="flyout-detail">
                        <div style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <Spinner
                                style={{ marginTop: '30px' }}
                                as="span"
                                animation="border"
                                role="status"
                                aria-hidden="true"
                                variant="info"
                            />
                        </div>
                    </div>
                </Col>
            </Row>
        );
    }

    if (error) {
        return (
            <Row>
                <Col className="text-center mt-4">
                    <span style={{ color: APP_COLORS['gray-600'] }}>{error}</span>
                </Col>
            </Row>
        );
    }

    if (!record) return null;

    const {
        company_name,
        company_score,
        company_url,
        company_phone,
        company_email,
        company_address_delivery_line_1,
        company_address_city,
        company_address_region,
        company_address_postal_code,
    } = record;

    const cityRegion = [company_address_city, company_address_region].filter(Boolean).join(', ');
    const companyAddressLastLine = [cityRegion, company_address_postal_code].filter(Boolean).join(' ');

    const densityIcons = [
        { attr: 'company_url', icon: faGlobe },
        { attr: 'company_email', icon: faEnvelope },
        { attr: 'company_phone', icon: faPhone },
        { attr: 'company_address_delivery_line_1', icon: faMapMarkerAlt },
    ];

    const densityIconsJsx = densityIcons.map((i, idx) => {
        if (record[i.attr]) {
            if (i.attr === 'company_url' && company_url) {
                return (
                    <div key={idx} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: -5 }}>
                        <TextLink
                            href={`https://${company_url.replace(/(^\w+:|^)\/\//, '')}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <FontAwesomeIcon color={APP_COLORS['orange']} icon={i.icon} style={{ fontSize: '16px' }} />
                        </TextLink>
                    </div>
                );
            }

            if (i.attr === 'company_phone' && company_phone) {
                return (
                    <div key={idx} style={{ marginTop: -5 }}>
                        <TextLink
                            href={`tel:${company_phone}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <FontAwesomeIcon color={APP_COLORS['blue']} icon={i.icon} style={{ fontSize: '16px' }} />
                        </TextLink>
                    </div>
                );
            }

            if (i.attr === 'company_email' && company_email) {
                return (
                    <div key={idx} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: -5 }}>
                        <TextLink
                            href={`mailto:${company_email}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <FontAwesomeIcon color={APP_COLORS['blue']} icon={i.icon} style={{ fontSize: '16px' }} />
                        </TextLink>
                    </div>
                );
            }
        }
        return null;
    }).filter(Boolean);

    return (
        <Row>
            <Col>
                <div className="explore-map-flyout-detail explore-map-flyout-detail-data-point">
                    <div className={"flyout-detail-section"}>
                        <div className="icon-container">
                            <Image
                                className="icon"
                                src={companyIcon}
                                alt="Product Icon"
                                fluid
                            />
                            <div>
                                <div className="lead-text-container">
                                    <div className="lead-text-items">
                                        <span className="lead-text-label">
                                            <a>{company_name || 'Unknown Company'}</a>
                                            <ScoreText>{company_score ? Number(company_score).toFixed(2) : ''}</ScoreText>
                                        </span>
                                        <LeadTextSmall>{company_address_delivery_line_1}</LeadTextSmall>
                                        <LeadTextSmall>{companyAddressLastLine}</LeadTextSmall>
                                    </div>
                                </div>
                                <div>
                                    <TextLinkContainer>
                                        {densityIconsJsx.length > 0 && densityIconsJsx}
                                    </TextLinkContainer>
                                </div>
                            </div>
                        </div>
                    </div>
                    <ExploreMapFlyoutDetailUnCluster item={record} />
                </div>
            </Col>
        </Row>
    );
}

async function _getDataPointRecord(cubeNamespace, point) {
    const p = point?.properties || {};

    console.log(p);

    const companyId = p.companyId || p.id || p.company_identity || p.company_id || point?.id;

    const _filters = [];

    if (companyId) {
        _filters.push({
            member: `${cubeNamespace}.company_identity`,
            operator: 'equals',
            values: [`${companyId}`]
        });
    } else if (p.company_address_city && p.company_address_region) {
        _filters.push({
            member: `${cubeNamespace}.company_address_city`,
            operator: 'equals',
            values: [p.company_address_city]
        });
        _filters.push({
            member: `${cubeNamespace}.company_address_region`,
            operator: 'equals',
            values: [p.company_address_region]
        });
    } else {
        throw new Error('companyId and location data are missing from point data');
    }

    const payload = {
        dimensions: [
            `${cubeNamespace}.company_id`,
            `${cubeNamespace}.company_identity`,
            `${cubeNamespace}.company_score`,
            `${cubeNamespace}.company_name`,
            `${cubeNamespace}.company_url`,
            `${cubeNamespace}.company_address_delivery_line_1`,
            `${cubeNamespace}.company_address_city`,
            `${cubeNamespace}.company_address_region`,
            `${cubeNamespace}.company_address_postal_code`,
            `${cubeNamespace}.company_phone`,
            `${cubeNamespace}.company_email`,
            `${cubeNamespace}.company_class_trades`,
            `${cubeNamespace}.company_class_primary_trade`,
            `${cubeNamespace}.company_class_primary_trade_score`,
            `${cubeNamespace}.company_profile_manufacturer_profiles`,
            `${cubeNamespace}.company_profile_online_profiles`,
            `${cubeNamespace}.company_profile_reviews_ratings_avg`,
            `${cubeNamespace}.company_profile_association_profiles`,
            `${cubeNamespace}.company_source_cnt`,
            `${cubeNamespace}.company_authoritative_source_cnt`,
            `${cubeNamespace}.company_source_labels`,
            `${cubeNamespace}.company_source_first_touch`,
            `${cubeNamespace}.company_source_last_touch`,
            `${cubeNamespace}.company_profile_cnt`,
            `${cubeNamespace}.company_license_cnt`,
            `${cubeNamespace}.company_permit_cnt`,
            `${cubeNamespace}.company_policy_cnt`,
            `${cubeNamespace}.company_input_cnt`,
            `${cubeNamespace}.contact_id`,
            `${cubeNamespace}.contact_name_given_name`,
            `${cubeNamespace}.contact_name_surname`,
            `${cubeNamespace}.contact_title`,
            `${cubeNamespace}.contact_phone`,
            `${cubeNamespace}.contact_email`,
            `${cubeNamespace}.contact_email_status`,
            `${cubeNamespace}.contact_roles`,
            `${cubeNamespace}.company_contact_cnt`,
        ],
        timeDimensions: [],
        ungrouped: true,
        filters: _filters,
        limit: 1
    };

    const resultSet = await CubeApi.load(config.services.ATLAS.cubeBaseUrl, payload);

    if (!resultSet || resultSet.length !== 1) {
        console.error('returned unexpected result set (expected 1)', resultSet);
        throw new Error('company id not found');
    }

    const row = resultSet[0];

    return {
        company_identity: row[`${cubeNamespace}.company_identity`],
        company_score: row[`${cubeNamespace}.company_score`],
        company_name: row[`${cubeNamespace}.company_name`],
        company_url: row[`${cubeNamespace}.company_url`],
        company_address_delivery_line_1: row[`${cubeNamespace}.company_address_delivery_line_1`],
        company_address_city: row[`${cubeNamespace}.company_address_city`],
        company_address_region: row[`${cubeNamespace}.company_address_region`],
        company_address_postal_code: row[`${cubeNamespace}.company_address_postal_code`],
        company_phone: row[`${cubeNamespace}.company_phone`],
        company_email: row[`${cubeNamespace}.company_email`],
        company_class_primary_trade: row[`${cubeNamespace}.company_class_primary_trade`],
        company_class_primary_trade_score: row[`${cubeNamespace}.company_class_primary_trade_score`],
        company_profile_manufacturer_profiles: row[`${cubeNamespace}.company_profile_manufacturer_profiles`],
        company_profile_online_profiles: row[`${cubeNamespace}.company_profile_online_profiles`],
        company_profile_reviews_ratings_avg: row[`${cubeNamespace}.company_profile_reviews_ratings_avg`],
        company_profile_association_profiles: row[`${cubeNamespace}.company_profile_association_profiles`],
        company_profile_utilities: null,
        company_stat_contact_cnt: row[`${cubeNamespace}.company_contact_cnt`],
        company_input_cnt: row[`${cubeNamespace}.company_input_cnt`],
        company_source_cnt: row[`${cubeNamespace}.company_source_cnt`],
        company_authoritative_source_cnt: row[`${cubeNamespace}.company_authoritative_source_cnt`],
        company_source_labels: row[`${cubeNamespace}.company_source_labels`],
        company_source_first_touch: row[`${cubeNamespace}.company_source_first_touch`],
        company_source_last_touch: row[`${cubeNamespace}.company_source_last_touch`],
        company_profile_cnt: row[`${cubeNamespace}.company_profile_cnt`],
        company_license_cnt: row[`${cubeNamespace}.company_license_cnt`],
        company_permit_cnt: row[`${cubeNamespace}.company_permit_cnt`],
        company_policy_cnt: row[`${cubeNamespace}.company_policy_cnt`],
        contact_id: row[`${cubeNamespace}.contact_id`],
        contact_name_given_name: row[`${cubeNamespace}.contact_name_given_name`],
        contact_name_surname: row[`${cubeNamespace}.contact_name_surname`],
        contact_title: row[`${cubeNamespace}.contact_title`],
        company_class_trades: row[`${cubeNamespace}.company_class_trades`],
        companyProfileCnt: row[`${cubeNamespace}.company_profile_cnt`],
        companyLicenseCnt: row[`${cubeNamespace}.company_license_cnt`],
        companyPolicyCnt: row[`${cubeNamespace}.company_policy_cnt`],
        companyPermitCnt: row[`${cubeNamespace}.company_permit_cnt`],
        companyStatContactCnt: row[`${cubeNamespace}.company_contact_cnt`],
        companySourceCnt: row[`${cubeNamespace}.company_source_cnt`],
        contactPhone: row[`${cubeNamespace}.contact_phone`],
        contactEmail: row[`${cubeNamespace}.contact_email`],
        contactEmailStatus: row[`${cubeNamespace}.contact_email_status`],
    };
}
